/**
 * Admin Company Management Page
 * Comprehensive company verification and management system
 */

import React, { useState, useEffect } from 'react';
import { 
  Building, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Search, 
  Filter, 
  Eye, 
  ExternalLink,
  AlertTriangle,
  User,
  Calendar,
  Globe,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  MoreVertical,
  Ban,
  RotateCcw,
  Download
} from 'lucide-react';
import { 
  getCompanies, 
  getPendingCompanies, 
  updateCompanyVerification,
  updateCompanySuspension,
  getCompanyDetails,
  searchCompanies 
} from '../services/adminService';

const AdminCompaniesPage = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTab, setSelectedTab] = useState('pending');
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    industry: 'all',
    status: 'all'
  });
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [actionLoading, setActionLoading] = useState({});

  useEffect(() => {
    fetchCompanies();
  }, [selectedTab, filters]);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      setError(null);
      
      let companiesData;
      
      if (selectedTab === 'pending') {
        companiesData = await getPendingCompanies({ limitCount: 50 });
      } else if (selectedTab === 'all') {
        companiesData = await getCompanies({ limitCount: 50 });
      } else {
        // For verified/rejected, use getCompanies with status filter
        companiesData = await getCompanies({ 
          verified: selectedTab === 'verified',
          limitCount: 50 
        });
      }

      setCompanies(companiesData.companies || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
      setError('Failed to load companies. Please check your permissions and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (companyId, verified, reason = '') => {
    try {
      setActionLoading(prev => ({ 
        ...prev, 
        [companyId]: verified ? 'verifying' : 'rejecting' 
      }));
      
      await updateCompanyVerification(companyId, verified, reason);
      
      // Remove from current list
      setCompanies(prev => prev.filter(c => c.id !== companyId));
      setActionLoading(prev => ({ ...prev, [companyId]: null }));
      
    } catch (error) {
      console.error('Error updating company verification:', error);
      setActionLoading(prev => ({ ...prev, [companyId]: null }));
      setError('Failed to update company verification. Please try again.');
    }
  };

  const handleSuspend = async (companyId, suspended, reason = '') => {
    try {
      setActionLoading(prev => ({ 
        ...prev, 
        [companyId]: suspended ? 'suspending' : 'reactivating' 
      }));
      
      await updateCompanySuspension(companyId, suspended, reason);
      
      // Update company in the list
      setCompanies(prev => prev.map(c => 
        c.id === companyId 
          ? { ...c, suspended, suspensionReason: suspended ? reason : null }
          : c
      ));
      setActionLoading(prev => ({ ...prev, [companyId]: null }));
      
    } catch (error) {
      console.error('Error updating company suspension:', error);
      setActionLoading(prev => ({ ...prev, [companyId]: null }));
      setError('Failed to update company suspension. Please try again.');
    }
  };

  const handleViewDetails = async (companyId) => {
    try {
      setLoading(true);
      const companyDetails = await getCompanyDetails(companyId);
      setSelectedCompany(companyDetails);
      setShowDetailsModal(true);
    } catch (error) {
      console.error('Error fetching company details:', error);
      setError('Failed to load company details.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (company) => {
    if (company.suspended) {
      return <span className="bg-red-100 text-red-800 text-xs font-medium px-2.5 py-0.5 rounded-full">Suspended</span>;
    }
    if (company.verified) {
      return <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full">Verified</span>;
    }
    return <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2.5 py-0.5 rounded-full">Pending</span>;
  };

  const filteredCompanies = companies.filter(company => {
    if (searchQuery && !company.companyName?.toLowerCase().includes(searchQuery.toLowerCase()) && 
        !company.email?.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !company.contactPerson?.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    if (filters.industry !== 'all' && company.industry !== filters.industry) {
      return false;
    }
    return true;
  });

  const tabs = [
    { 
      id: 'pending', 
      name: 'Pending Verification', 
      count: companies.filter(c => !c.verified && !c.suspended).length,
      icon: <Clock size={16} />
    },
    { 
      id: 'verified', 
      name: 'Verified', 
      count: companies.filter(c => c.verified && !c.suspended).length,
      icon: <CheckCircle size={16} />
    },
    { 
      id: 'suspended', 
      name: 'Suspended', 
      count: companies.filter(c => c.suspended).length,
      icon: <Ban size={16} />
    },
    { 
      id: 'all', 
      name: 'All Companies', 
      count: companies.length,
      icon: <Building size={16} />
    }
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Company Management</h1>
          <p className="text-gray-600 mt-1">Verify and manage company accounts</p>
        </div>
        <div className="flex items-center space-x-3">
          <button className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors">
            <Download size={16} />
            <span>Export Data</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pending Verification</p>
              <p className="text-2xl font-bold text-yellow-600">{companies.filter(c => !c.verified && !c.suspended).length}</p>
            </div>
            <div className="bg-yellow-100 p-3 rounded-full">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Verified</p>
              <p className="text-2xl font-bold text-green-600">{companies.filter(c => c.verified && !c.suspended).length}</p>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Suspended</p>
              <p className="text-2xl font-bold text-red-600">{companies.filter(c => c.suspended).length}</p>
            </div>
            <div className="bg-red-100 p-3 rounded-full">
              <Ban className="h-6 w-6 text-red-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Companies</p>
              <p className="text-2xl font-bold text-gray-900">{companies.length}</p>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <Building className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md border border-gray-200 space-y-4">
        {/* Search */}
        <div className="relative">
          <Search className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search companies by name, email, or contact person..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <select
            value={filters.industry}
            onChange={(e) => setFilters(prev => ({ ...prev, industry: e.target.value }))}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
          >
            <option value="all">All Industries</option>
            <option value="Technology">Technology</option>
            <option value="Finance">Finance</option>
            <option value="Healthcare">Healthcare</option>
            <option value="Education">Education</option>
            <option value="Manufacturing">Manufacturing</option>
            <option value="Retail">Retail</option>
            <option value="Other">Other</option>
          </select>

          <select
            value={filters.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
          >
            <option value="all">All Statuses</option>
            <option value="verified">Verified</option>
            <option value="pending">Pending</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setSelectedTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex items-center space-x-2 ${
                selectedTab === tab.id
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.icon}
              <span>{tab.name}</span>
              <span className="ml-2 bg-gray-100 text-gray-900 py-0.5 px-2.5 rounded-full text-xs">
                {tab.count}
              </span>
            </button>
          ))}
        </nav>
      </div>

      {/* Companies List */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white p-6 rounded-lg shadow-md border border-gray-200 animate-pulse">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                </div>
                <div className="h-8 bg-gray-200 rounded w-20"></div>
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <AlertTriangle className="h-8 w-8 text-red-500 mx-auto mb-2" />
          <p className="text-red-700">{error}</p>
          <button
            onClick={fetchCompanies}
            className="mt-2 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg transition-colors"
          >
            Retry
          </button>
        </div>
      ) : filteredCompanies.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-12 text-center">
          <Building className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No companies found</h3>
          <p className="text-gray-600">No companies match your current filters.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredCompanies.map((company) => (
            <div key={company.id} className="bg-white p-4 sm:p-6 rounded-lg shadow-md border border-gray-200">
              <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 mt-1">
                      <div className="bg-green-100 p-3 rounded-lg">
                        <Building className="h-6 w-6 text-green-600" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900 truncate">{company.companyName}</h3>
                        {getStatusBadge(company)}
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-600">
                        <div className="flex items-center space-x-2">
                          <User className="h-4 w-4 flex-shrink-0" />
                          <span className="truncate">{company.contactPerson || 'N/A'}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Mail className="h-4 w-4 flex-shrink-0" />
                          <span className="truncate">{company.email}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Briefcase className="h-4 w-4 flex-shrink-0" />
                          <span className="truncate">{company.industry || 'N/A'}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Calendar className="h-4 w-4 flex-shrink-0" />
                          <span>Registered {company.createdAt?.toLocaleDateString()}</span>
                        </div>
                      </div>

                      {company.stats && (
                        <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-gray-500">
                          <span className="bg-gray-100 px-2 py-1 rounded text-xs">
                            {company.stats.totalJobs} Jobs
                          </span>
                          <span className="bg-gray-100 px-2 py-1 rounded text-xs">
                            {company.stats.activeJobs} Active
                          </span>
                          <span className="bg-gray-100 px-2 py-1 rounded text-xs">
                            {company.stats.totalApplications} Applications
                          </span>
                        </div>
                      )}

                      {company.description && (
                        <p className="text-sm text-gray-600 mt-3 line-clamp-2">{company.description}</p>
                      )}

                      {company.rejectionReason && (
                        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                          <p className="text-sm text-red-700">
                            <strong>Rejection Reason:</strong> {company.rejectionReason}
                          </p>
                        </div>
                      )}

                      {company.suspensionReason && (
                        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                          <p className="text-sm text-red-700">
                            <strong>Suspension Reason:</strong> {company.suspensionReason}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center space-x-2 flex-shrink-0">
                  <button
                    onClick={() => handleViewDetails(company.id)}
                    className="p-2 text-gray-400 hover:text-green-600 rounded-lg hover:bg-gray-100 transition-colors"
                    title="View Details"
                  >
                    <Eye size={18} />
                  </button>
                  
                  {company.website && (
                    <a
                      href={company.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-gray-400 hover:text-green-600 rounded-lg hover:bg-gray-100 transition-colors"
                      title="Visit Website"
                    >
                      <ExternalLink size={18} />
                    </a>
                  )}

                  {!company.verified && !company.suspended && (
                    <>
                      <button
                        onClick={() => handleVerify(company.id, true)}
                        disabled={actionLoading[company.id]}
                        className="bg-green-500 hover:bg-green-600 text-white px-3 py-1.5 rounded-lg text-sm transition-colors disabled:opacity-50"
                      >
                        {actionLoading[company.id] === 'verifying' ? 'Verifying...' : 'Verify'}
                      </button>
                      
                      <button
                        onClick={() => handleVerify(company.id, false, 'Failed verification requirements')}
                        disabled={actionLoading[company.id]}
                        className="bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded-lg text-sm transition-colors disabled:opacity-50"
                      >
                        {actionLoading[company.id] === 'rejecting' ? 'Rejecting...' : 'Reject'}
                      </button>
                    </>
                  )}

                  {company.verified && (
                    <>
                      {company.suspended ? (
                        <button
                          onClick={() => handleSuspend(company.id, false)}
                          disabled={actionLoading[company.id]}
                          className="bg-green-500 hover:bg-green-600 text-white px-3 py-1.5 rounded-lg text-sm transition-colors disabled:opacity-50 flex items-center space-x-1"
                        >
                          <RotateCcw size={14} />
                          <span>{actionLoading[company.id] === 'reactivating' ? 'Reactivating...' : 'Reactivate'}</span>
                        </button>
                      ) : (
                        <button
                          onClick={() => handleSuspend(company.id, true, 'Administrative suspension')}
                          disabled={actionLoading[company.id]}
                          className="bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded-lg text-sm transition-colors disabled:opacity-50 flex items-center space-x-1"
                        >
                          <Ban size={14} />
                          <span>{actionLoading[company.id] === 'suspending' ? 'Suspending...' : 'Suspend'}</span>
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Company Details Modal */}
      {showDetailsModal && selectedCompany && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Company Details</h2>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle size={24} />
                </button>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Company Information */}
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Company Information</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-gray-600">Company Name</label>
                        <p className="text-gray-900">{selectedCompany.companyName}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Contact Person</label>
                        <p className="text-gray-900">{selectedCompany.contactPerson || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Email</label>
                        <p className="text-gray-900">{selectedCompany.email}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Industry</label>
                        <p className="text-gray-900">{selectedCompany.industry || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Company Size</label>
                        <p className="text-gray-900">{selectedCompany.companySize || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Website</label>
                        {selectedCompany.website ? (
                          <a 
                            href={selectedCompany.website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-green-600 hover:text-green-700 flex items-center space-x-1"
                          >
                            <span>{selectedCompany.website}</span>
                            <ExternalLink size={14} />
                          </a>
                        ) : (
                          <p className="text-gray-900">N/A</p>
                        )}
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Location</label>
                        <p className="text-gray-900">{selectedCompany.location || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Description</label>
                        <p className="text-gray-900">{selectedCompany.description || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Statistics and Recent Activity */}
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Statistics</h3>
                    {selectedCompany.stats && (
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <p className="text-sm text-gray-600">Total Jobs</p>
                          <p className="text-xl font-bold text-gray-900">{selectedCompany.stats.totalJobs}</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <p className="text-sm text-gray-600">Active Jobs</p>
                          <p className="text-xl font-bold text-green-600">{selectedCompany.stats.activeJobs}</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <p className="text-sm text-gray-600">Applications</p>
                          <p className="text-xl font-bold text-gray-900">{selectedCompany.stats.totalApplications}</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <p className="text-sm text-gray-600">Response Rate</p>
                          <p className="text-xl font-bold text-blue-600">{selectedCompany.stats.responseRate}%</p>
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Recent Job Postings</h3>
                    <div className="space-y-2">
                      {selectedCompany.recentJobs?.length > 0 ? (
                        selectedCompany.recentJobs.map((job) => (
                          <div key={job.id} className="p-3 border border-gray-200 rounded-lg">
                            <p className="font-medium text-gray-900">{job.title}</p>
                            <p className="text-sm text-gray-600">Posted {job.createdAt.toLocaleDateString()}</p>
                            <span className={`inline-block px-2 py-1 text-xs rounded ${
                              job.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                              {job.status}
                            </span>
                          </div>
                        ))
                      ) : (
                        <p className="text-gray-500 text-sm">No recent job postings</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Account Status</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Verification Status:</span>
                        {getStatusBadge(selectedCompany)}
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Registration Date:</span>
                        <span className="text-sm text-gray-900">{selectedCompany.createdAt?.toLocaleDateString()}</span>
                      </div>
                      {selectedCompany.verificationDate && (
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Verification Date:</span>
                          <span className="text-sm text-gray-900">{selectedCompany.verificationDate.toLocaleDateString()}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-6 border-t mt-6">
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
                {!selectedCompany.verified && (
                  <>
                    <button
                      onClick={() => {
                        handleVerify(selectedCompany.id, true);
                        setShowDetailsModal(false);
                      }}
                      className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
                    >
                      Verify Company
                    </button>
                    <button
                      onClick={() => {
                        handleVerify(selectedCompany.id, false, 'Failed verification requirements');
                        setShowDetailsModal(false);
                      }}
                      className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                    >
                      Reject Company
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCompaniesPage;